import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from "react";
import { FileUploader, SCheckboxV2, SOverlaySpinner, useEditorDependencyInjectionContext, SVariablePicker } from "../..";
import { SSlider } from "../../SSlider";
import { SButton, SField, SIcon } from "../../components";
import { SInputV2 } from "../../SInputV2";
import { SWStateRecordType } from "@superwall-me/schema";
export const LottiePropertyEditor = ({ value, readOnly, onClickValue, onChangeValue })=>{
    const [selectedFile, setSelectedFile] = useState();
    const uploadContext = useEditorDependencyInjectionContext();
    const { useVariablePickerItems, requiresLiquid, getRequiredPaths } = useEditorDependencyInjectionContext();
    const items = useVariablePickerItems();
    const [hasFocus, setHasFocus] = useState(false);
    const inputRef = useRef(null);
    const { mutateAsync: upload, isError, error, isLoading, reset: resetUploadFn } = uploadContext.useUploadFile();
    const defaultPlaceholder = "e.g. http://example.com/animation.json";
    const placeholder = defaultPlaceholder;
    const setLottieProps = useCallback((newProps)=>{
        const updatedValue = {
            type: "property-lottie",
            src: typeof value?.src !== "undefined" ? value?.src : "",
            autoplay: typeof value?.autoplay !== "undefined" ? value?.autoplay : false,
            loop: typeof value?.loop !== "undefined" ? value?.loop : false,
            controls: typeof value?.controls !== "undefined" ? value?.controls : false,
            keepLastFrame: typeof value?.keepLastFrame !== "undefined" ? value?.keepLastFrame : false,
            speed: typeof value?.speed !== "undefined" ? value?.speed : 1.0,
            ...value,
            ...newProps
        };
        if (requiresLiquid(updatedValue.src)) {
            const requiredRecords = getRequiredPaths(updatedValue.src).map((x)=>SWStateRecordType.createId(x));
            onChangeValue && onChangeValue({
                ...updatedValue,
                type: "property-lottie",
                rendering: {
                    type: "liquid",
                    requiredStateIds: requiredRecords
                }
            });
        } else {
            onChangeValue && onChangeValue({
                ...updatedValue,
                type: "property-lottie",
                rendering: {
                    type: "literal"
                }
            });
        }
    }, [
        onChangeValue,
        value
    ]);
    const uploadFile = async (file)=>{
        setSelectedFile(file);
        try {
            const resp = await upload({
                file
            });
            const { url } = resp.result;
            setLottieProps({
                src: url
            });
        } catch (e) {}
    };
    const onHandleUpload = async (file)=>{
        setSelectedFile(file);
        await uploadFile(file);
    };
    const onRetryUpload = async (e)=>{
        e.preventDefault();
        await uploadFile(selectedFile);
    };
    const onInputChange = (e)=>{
        e.preventDefault();
        setLottieProps({
            src: e.target.value
        });
        if (isError) resetUploadFn();
    };
    return /*#__PURE__*/ _jsx("div", {
        onClick: onClickValue,
        children: /*#__PURE__*/ _jsx("div", {
            className: readOnly ? "pointer-events-none" : "",
            children: /*#__PURE__*/ _jsxs(SField, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-[1fr,auto] items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SInputV2, {
                                value: value?.src || "",
                                onChange: onInputChange,
                                placeholder: placeholder,
                                ref: inputRef,
                                onFocus: ()=>setHasFocus(true),
                                onBlur: ()=>setTimeout(()=>{
                                        setHasFocus(false);
                                    }, 1000)
                            }),
                            /*#__PURE__*/ _jsx(FileUploader, {
                                ariaLabel: "Lottie Upload",
                                handleFile: onHandleUpload,
                                // only accepts json
                                accept: "application/json",
                                hasError: !!error,
                                isSuiButton: true,
                                suiButtonProps: {
                                    variant: "primary",
                                    className: "h-[34px] !rounded-md "
                                },
                                handleRetry: onRetryUpload
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between pt-3 pb-3 gap-3",
                        children: [
                            /*#__PURE__*/ _jsx(SCheckboxV2, {
                                value: value?.loop,
                                className: "w-fit min-w-0 shrink-0",
                                onChange: (loop)=>{
                                    setLottieProps({
                                        loop
                                    });
                                },
                                children: "Loop"
                            }),
                            /*#__PURE__*/ _jsx(SCheckboxV2, {
                                value: value?.keepLastFrame,
                                className: "w-fit min-w-0 shrink-0",
                                onChange: (keepLastFrame)=>{
                                    setLottieProps({
                                        keepLastFrame
                                    });
                                },
                                children: "Keep last frame"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "w-fit min-w-0 shrink-0 -ml-5",
                                children: /*#__PURE__*/ _jsx(SVariablePicker, {
                                    items: items,
                                    asButton: true,
                                    enableHotkey: hasFocus,
                                    didSelect: (item)=>{
                                        let text = value?.src || "";
                                        if (inputRef.current) {
                                            const { selectionStart, selectionEnd } = inputRef.current;
                                            const beforeText = text.substring(0, selectionStart || 0).trimEnd();
                                            const textToInsert = item.liquid?.trim() || "";
                                            const rhs = text.substring(selectionEnd || 0, text.length).trimStart();
                                            const lhs = `${beforeText}${textToInsert}`;
                                            const newPosition = lhs.length;
                                            text = `${lhs}${rhs}`;
                                            setLottieProps({
                                                src: text
                                            });
                                            setTimeout(()=>{
                                                inputRef.current?.focus();
                                                if (inputRef.current) {
                                                    inputRef.current.selectionStart = newPosition;
                                                    inputRef.current.selectionEnd = newPosition;
                                                }
                                            }, 100);
                                        } else {
                                            setLottieProps({
                                                src: `${value?.src || ""}${item.liquid?.trim()}`
                                            });
                                        }
                                    },
                                    didClose: ()=>{}
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-[auto,auto,1fr,auto] items-center justify-start gap-3 pr-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                children: "Speed"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm opacity-50 w-[25px]",
                                children: `x${(value?.speed || 1.0).toFixed(1)}`
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "px-2",
                                children: /*#__PURE__*/ _jsx(SSlider, {
                                    min: 0.1,
                                    max: 4.0,
                                    className: "!w-full",
                                    step: 0.1,
                                    value: value?.speed || 1.0,
                                    onChange: (value)=>setLottieProps({
                                            speed: value
                                        })
                                })
                            }),
                            /*#__PURE__*/ _jsx(SButton, {
                                isMinimal: true,
                                disabled: value?.speed === 1.0,
                                className: `!p-0 !m-0 ${value?.speed === 1.0 ? "!text-white !opacity-40" : ""}`,
                                onClick: ()=>setLottieProps({
                                        speed: 1.0
                                    }),
                                children: /*#__PURE__*/ _jsx(SIcon, {
                                    type: "refresh",
                                    size: 16
                                })
                            })
                        ]
                    }),
                    isError && /*#__PURE__*/ _jsx("div", {
                        children: error?.message
                    }),
                    value?.src && !value?.src.startsWith("https://user-content.superwalleditor.com/") && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm opacity-50",
                        children: "External Lottie URLs may impact performance and incur CDN costs. For best results, upload Lottie files directly to Superwall."
                    }),
                    isLoading && /*#__PURE__*/ _jsx(SOverlaySpinner, {})
                ]
            })
        })
    });
};
